/**
 * Agent guard (ADR-0048). Wraps `RequireAuth` and checks the caller may use
 * at least one of the given agents, from `/me`'s `agents`.
 *
 * Agents are reachable only through a grant — on the agent or on its
 * Solution — except for the Administrator, who reaches them all. A direct link
 * to an agent the person was not given renders `ForbiddenPage` naming the
 * agent and whom to ask, rather than an empty page. UI only: every agent route
 * is gated server-side (`requires_agent`), and a refusal there is audited.
 */

import type { PropsWithChildren } from "react";
import { useMe } from "@/features/users/useMe";
import { agentName, agentOffered } from "./agents";
import { ForbiddenPage } from "./ForbiddenPage";
import { RequireAuth } from "./RequireAuth";

interface Props {
  /** One agent key, or several when a page serves more than one agent. */
  agent: string | readonly string[];
}

export function RequireAgent({ agent, children }: PropsWithChildren<Props>) {
  return (
    <RequireAuth>
      <RequireAgentInner agent={agent}>{children}</RequireAgentInner>
    </RequireAuth>
  );
}

function RequireAgentInner({ agent, children }: PropsWithChildren<Props>) {
  const { data, loading } = useMe();
  if (loading) return <p>Checking access…</p>;
  const wanted = typeof agent === "string" ? [agent] : agent;
  const reachable = data?.agents ?? [];
  if (!wanted.some((key) => reachable.includes(key))) {
    const offered = wanted.some((key) => agentOffered(key, data?.deployment_solutions));
    return <ForbiddenPage agentName={agentName(wanted[0] ?? "")} notOffered={!offered} />;
  }
  return <>{children}</>;
}
